import { useEffect, useRef, useState } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { KpiCard } from '../../components/kpi/KpiCard';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { MultiLineChart } from '../../components/charts/MultiLineChart';
import { ProductPicker } from '../../components/charts/ProductPicker';
import { BarChart } from '../../components/charts/BarChart';
import { PieChart } from '../../components/charts/PieChart';
import { ShareBar } from '../../components/charts/ShareBar';
import { TrendBadge } from '../../components/ui/Badge';
import { DateRangePicker } from '../../components/filters/DateRangePicker';
import { AskWidget } from '../../components/ask/AskWidget';
import { useApiData } from '../../hooks/useApiData';
import { useOptions } from '../../hooks/useOptions';
import { useDataRefresh } from '../../context/DataRefreshContext';
import { useAuth } from '../../hooks/useAuth';
import { isAdminOrAbove } from '../../lib/roles';
import { CHART_COLORS } from '../../config/constants';
import { colorForProduct } from '../../config/productColors';
import { lastDaysRange, dayTickLabel } from '../../utils/periods';
import { formatNumber, formatPercent } from '../../utils/formatters';

// Pivot long-format [{period, product, total}] into chart rows [{period, ...perProduct}]
// for the given periods (x-axis) and selected products (series). Missing cells stay
// undefined so Recharts just skips them.
function pivotByPeriod(rows, periods, selected) {
  const sel = new Set(selected);
  const map = new Map(periods.map((p) => [p, { period: p }]));
  rows.forEach((r) => {
    if (map.has(r.period) && sel.has(r.product)) map.get(r.period)[r.product] = r.total;
  });
  return periods.map((p) => map.get(p));
}

// Month/Year period → { from, to }. Month 'YYYY-MM' → 1st..end-of-month; Year 'YYYY' → Jan 1..Dec 31.
function periodToRange(mode, value) {
  if (!value) return { from: '', to: '' };
  if (mode === 'year') return { from: `${value}-01-01`, to: `${value}-12-31` };
  const [y, m] = value.split('-').map(Number);
  const last = String(new Date(y, m, 0).getDate()).padStart(2, '0');
  return { from: `${value}-01`, to: `${value}-${last}` };
}

// 'YYYY-MM' → "July 2026" for the period label.
function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// Short month for the x-axis: 'YYYY-MM' → "Jun".
function monthTick(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1).toLocaleDateString(undefined, { month: 'short' });
}

export default function Dashboard() {
  const { profile } = useAuth();
  const { prefetch } = useDataRefresh();
  const { dateRange } = useOptions();
  const isAdmin = isAdminOrAbove(profile);

  // Month selector → scopes the breakdowns (Product Family Breakdown, Top 5 Products,
  // Products-in-family) + Top 5 Resellers to that month's average-per-active-day.
  // Total Usage / Top Product stay latest-day (unaffected).
  const [monthVal, setMonthVal] = useState('');
  useEffect(() => {
    if (dateRange?.max && !monthVal) setMonthVal(dateRange.max.slice(0, 7));
  }, [dateRange, monthVal]);
  const periodLabel = monthLabel(monthVal);
  const { from, to } = periodToRange('month', monthVal);

  const { data, loading, error } = useApiData('/api/data/dashboard', { from, to });
  const families = data?.familyBreakdown || [];

  const [family, setFamily] = useState('');

  // Default the drill-down to the biggest family once data arrives.
  useEffect(() => {
    if (!family && families.length) setFamily(families[0].name);
  }, [family, families]);

  // Warm the top families' product drill-downs in the background (after load).
  useEffect(() => {
    if (loading) return;
    families.slice(0, 5).forEach((f) => prefetch('/api/data/family-products', { family: f.name, from, to }));
  }, [loading, families, prefetch, from, to]);

  const { data: familyProducts, loading: fpLoading } =
    useApiData('/api/data/family-products', { family, from, to });

  // Usage-trend charts — one colored line per product, top 8 by default + a picker.
  // Daily and Monthly share one product list + color map (so a product is the same
  // color in both), but keep independent selections and hover-focus state.
  const DEFAULT_LINES = 5;
  const productList = data?.productList || [];
  const productDailyTrend = data?.productDailyTrend || [];
  const productMonthlyTrend = data?.productMonthlyTrend || [];
  const colorFor = {};
  productList.forEach((name) => { colorFor[name] = colorForProduct(name); });

  // Daily — its own From/To (default last 7 days). Products default to top N once.
  const [dailyFrom, setDailyFrom] = useState('');
  const [dailyTo, setDailyTo] = useState('');
  useEffect(() => {
    if (!dailyFrom && dateRange?.max) {
      const r = lastDaysRange(7, dateRange.max);
      setDailyFrom(r.from); setDailyTo(r.to);
    }
  }, [dailyFrom, dateRange]);
  const [dailySel, setDailySel] = useState([]);
  const [dailyActive, setDailyActive] = useState(null);
  const dailyInit = useRef(false);
  useEffect(() => {
    if (!dailyInit.current && productList.length) {
      setDailySel(productList.slice(0, DEFAULT_LINES));
      dailyInit.current = true;
    }
  }, [productList]);
  const toggleDaily = (name) => setDailySel((c) => (c.includes(name) ? c.filter((n) => n !== name) : [...c, name]));
  const dailyDates = [...new Set(productDailyTrend.map((d) => d.period))].sort()
    .filter((p) => (!dailyFrom || p >= dailyFrom) && (!dailyTo || p <= dailyTo));
  const dailyData = pivotByPeriod(productDailyTrend, dailyDates, dailySel);
  const dailySeries = productList.filter((n) => dailySel.includes(n));

  // Monthly — pick a year; show that year's months. Products default to top N once.
  const yearOptions = [...new Set(productMonthlyTrend.map((d) => d.period.slice(0, 4)))].sort().reverse();
  const monthOptions = [...new Set(productMonthlyTrend.map((d) => d.period))].sort().reverse();
  const [monthlyYear, setMonthlyYear] = useState('');
  useEffect(() => {
    if (!monthlyYear && yearOptions.length) setMonthlyYear(yearOptions[0]);
  }, [monthlyYear, yearOptions]);
  const [monthlySel, setMonthlySel] = useState([]);
  const [monthlyActive, setMonthlyActive] = useState(null);
  const monthlyInit = useRef(false);
  useEffect(() => {
    if (!monthlyInit.current && productList.length) {
      setMonthlySel(productList.slice(0, DEFAULT_LINES));
      monthlyInit.current = true;
    }
  }, [productList]);
  const toggleMonthly = (name) => setMonthlySel((c) => (c.includes(name) ? c.filter((n) => n !== name) : [...c, name]));
  const monthlyPeriods = [...new Set(productMonthlyTrend.map((d) => d.period))].sort()
    .filter((p) => p.slice(0, 4) === monthlyYear);
  const monthlyData = pivotByPeriod(productMonthlyTrend, monthlyPeriods, monthlySel);
  const monthlySeries = productList.filter((n) => monthlySel.includes(n));

  // Which top reseller's product breakdown is expanded (accordion).
  const [expandedReseller, setExpandedReseller] = useState('');

  return (
    <AppShell title="Dashboard">
      {loading && <Spinner />}
      {error && <Alert>{error}</Alert>}
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard
              label="Total Usage"
              value={formatNumber(data.kpis.totalUsage)}
              badge={<TrendBadge value={data.kpis.totalUsageChange} />}
              sub="latest day · vs. previous day"
            />
            <KpiCard label="Top Product Family" value={data.kpis.topProduct} />
            <KpiCard label="Active Resellers" value={formatNumber(data.kpis.activeResellers)} />
            <KpiCard label="Active Product Families" value={formatNumber(data.kpis.productFamilies)} />
            <KpiCard label="Total Products" value={formatNumber(data.kpis.totalProducts)} sub="across all families" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card
              title="Monthly Usage Trend"
              subtitle={monthlyYear ? `avg per active day · ${monthlyYear}` : 'avg per active day'}
              actions={
                <div className="flex items-center gap-2">
                  <select
                    value={monthlyYear}
                    onChange={(e) => setMonthlyYear(e.target.value)}
                    className="rounded-lg border border-slate-300 dark:border-white/15 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                  >
                    {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <ProductPicker
                    products={productList}
                    selected={monthlySel}
                    colorFor={colorFor}
                    onToggle={toggleMonthly}
                    onSelectAll={() => setMonthlySel(productList)}
                    onClear={() => setMonthlySel([])}
                    onHover={setMonthlyActive}
                  />
                </div>
              }
            >
              {monthlyData.length && monthlySeries.length ? (
                <MultiLineChart
                  data={monthlyData}
                  series={monthlySeries}
                  colorFor={colorFor}
                  activeSeries={monthlyActive}
                  xTickFormatter={monthTick}
                  labelFormatter={monthLabel}
                  note="avg per active day"
                />
              ) : <EmptyState />}
            </Card>
            <Card
              title="Daily Usage Trend"
              subtitle={dailyFrom && dailyTo ? `total by day · ${dayTickLabel(dailyFrom)} – ${dayTickLabel(dailyTo)}` : 'total by day'}
              actions={
                <div className="flex flex-wrap items-center gap-1.5">
                  <DateRangePicker
                    from={dailyFrom}
                    to={dailyTo}
                    onChange={({ from, to }) => { setDailyFrom(from); setDailyTo(to); }}
                    min={dateRange?.min}
                    max={dateRange?.max}
                  />
                  <ProductPicker
                    products={productList}
                    selected={dailySel}
                    colorFor={colorFor}
                    onToggle={toggleDaily}
                    onSelectAll={() => setDailySel(productList)}
                    onClear={() => setDailySel([])}
                    onHover={setDailyActive}
                  />
                </div>
              }
            >
              {dailyData.length && dailySeries.length ? (
                <MultiLineChart
                  data={dailyData}
                  series={dailySeries}
                  colorFor={colorFor}
                  activeSeries={dailyActive}
                  xTickFormatter={dayTickLabel}
                />
              ) : <EmptyState />}
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <Card
              title="Product Family Breakdown"
              subtitle={periodLabel ? `avg per day · ${periodLabel}` : 'avg per day'}
              actions={
                <select
                  value={monthVal}
                  onChange={(e) => setMonthVal(e.target.value)}
                  className="rounded-lg border border-slate-300 dark:border-white/15 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                >
                  {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
              }
            >
              {families.length ? (
                <>
                  <ShareBar data={families} onSegmentClick={(name) => name && setFamily(name)} />
                  <PieChart
                    data={families}
                    height={240}
                    showLegend={false}
                    onSliceClick={(name) => name && setFamily(name)}
                  />
                  <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                    {families.map((f, i) => {
                      const selected = f.name === family;
                      return (
                        <li key={f.name}>
                          <button
                            type="button"
                            onClick={() => setFamily(f.name)}
                            className={`flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left transition-colors ${
                              selected ? 'bg-brand/5 dark:bg-brand/15 ring-1 ring-brand/20 dark:ring-brand/30' : 'hover:bg-slate-50 dark:hover:bg-white/5'
                            }`}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                                style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                              />
                              <span className={`truncate text-sm ${selected ? 'font-medium text-navy-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>
                                {f.name}
                              </span>
                            </span>
                            <span className="shrink-0 text-xs font-medium text-slate-400 dark:text-slate-500">
                              {formatPercent(f.share)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : <EmptyState />}
            </Card>
            <Card title="Top 5 Products" subtitle={periodLabel ? `avg per day · ${periodLabel}` : 'avg per day'} bodyClassName="flex flex-col">
              {data.top5Products.length
                ? (
                  <div className="flex h-full min-h-[300px] flex-col justify-center">
                    <BarChart
                      data={data.top5Products}
                      layout="vertical"
                      height="100%"
                      onBarClick={(d) => d?.name && setFamily(d.name)}
                    />
                  </div>
                )
                : <EmptyState />}
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <Card
              title={`Products in ${family || '—'}`}
              subtitle={periodLabel ? `avg per day · ${periodLabel}` : 'avg per day'}
              actions={
                <div className="w-44">
                  <Select
                    value={family}
                    onChange={(e) => setFamily(e.target.value)}
                    options={families.map((f) => f.name)}
                  />
                </div>
              }
            >
              {fpLoading && <Spinner />}
              {!fpLoading && (familyProducts && familyProducts.length
                ? (
                  <>
                    <BarChart
                      data={familyProducts}
                      layout="vertical"
                      height={Math.max(220, familyProducts.length * 44)}
                    />
                    <div className="mt-4">
                      <Table
                        columns={[
                          { key: 'name', label: 'Product' },
                          { key: 'total', label: 'Usage (avg/day)', align: 'right', render: (v) => formatNumber(v) },
                          { key: 'share', label: 'Share', align: 'right', render: (v) => formatPercent(v) },
                        ]}
                        rows={familyProducts}
                        pageSize={10}
                      />
                    </div>
                  </>
                )
                : <EmptyState />)}
            </Card>
          </div>

          {isAdmin && (
            <Card title="Top 5 Resellers" subtitle={periodLabel ? `avg per day · ${periodLabel}` : 'avg per day'}>
              {/* Column header (aligns with the rows below) */}
              <div className="flex items-center gap-4 px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <span className="w-6" />
                <span className="flex-1">Reseller</span>
                <span className="w-28 text-right">Usage</span>
                <span className="hidden sm:block w-16 text-right">Share</span>
              </div>

              <div className="space-y-2">
                {data.top5Resellers.map((r, i) => {
                  const open = expandedReseller === r.name;
                  return (
                    <div key={r.name} className="overflow-hidden rounded-xl ring-1 ring-slate-900/5 dark:ring-white/10">
                      <button
                        type="button"
                        onClick={() => setExpandedReseller(open ? '' : r.name)}
                        className={`flex w-full items-center gap-4 px-4 py-3 text-left transition ${open ? 'bg-brand/5 dark:bg-white/5' : 'hover:bg-slate-50 dark:hover:bg-white/5'}`}
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10 text-xs font-bold text-brand-dark dark:bg-brand/20 dark:text-brand-light">
                          {i + 1}
                        </span>
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          <span className="truncate font-medium text-navy-800 dark:text-slate-100">{r.name}</span>
                          <svg className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 5l5 5-5 5" />
                          </svg>
                        </span>
                        <span className="nums w-28 text-right font-medium text-navy-800 dark:text-slate-100">{formatNumber(r.total)}</span>
                        <span className="hidden sm:block w-16 text-right text-sm text-slate-500 dark:text-slate-400">{formatPercent(r.share)}</span>
                      </button>

                      {open && (
                        <div className="border-t border-slate-900/5 dark:border-white/10 bg-slate-50/70 dark:bg-white/[0.03] px-4 py-4">
                          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                            Products driving {r.name}
                          </div>
                          {r.products && r.products.length ? (
                            <ul className="space-y-2.5">
                              {r.products.map((p) => (
                                <li key={p.product} className="flex items-center gap-3">
                                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: colorForProduct(p.product) }} />
                                  <span className="w-32 shrink-0 truncate text-sm text-slate-600 dark:text-slate-300 sm:w-44">{p.product}</span>
                                  <div className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-slate-200/70 dark:bg-white/10">
                                    <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${p.share}%`, background: colorForProduct(p.product) }} />
                                  </div>
                                  <span className="nums w-24 text-right text-sm font-medium text-navy-800 dark:text-slate-100">{formatNumber(p.total)}</span>
                                  <span className="w-12 text-right text-xs text-slate-400 dark:text-slate-500">{p.share}%</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="text-sm text-slate-400 dark:text-slate-500">No product data.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Floating Ask assistant — Dashboard only, and admin-only (the widget
          hides itself for resellers; the API is gated server-side too). */}
      <AskWidget />
    </AppShell>
  );
}
